/* ============================================================================
 * Drool · Rewards bar — client-side runtime
 * ----------------------------------------------------------------------------
 * One small IIFE that drives every reward surface (the boxed top bar, the PDP
 * variant badges) from a single shared cart-total state.
 *
 * Architecture
 *   1. Each section/snippet outputs a root element with:
 *        data-drool-rewards="bar" | "vbadges"
 *        data-config='{...}'                  JSON config from Liquid
 *        data-variant-price="64.00"           (vbadges only) major-unit price
 *   2. On DOMContentLoaded the runtime finds those roots and initialises them.
 *   3. A single `/cart.js` fetch populates `cartTotalMajor`; every instance
 *      registers a render listener that re-runs when cart total changes.
 *   4. Cart-change detection layers (most → least specific):
 *        a. Custom events: cart:update[d] / cart:refresh / theme:cart:* / drool:cart:updated
 *        b. Transparent fetch interception of /cart/{add,change,update,clear}
 *        c. pageshow (handles bfcache restore)
 *
 * Performance notes
 *   • One fetch per cart-change, debounced to coalesce rapid events
 *   • Renderers cache the last HTML they emitted and skip the DOM write when
 *     nothing changed — keeps the work cheap when many instances are mounted
 *   • All listeners are passive where applicable; no MutationObserver
 *   • ~5KB gzipped, deferred load (set in Liquid via <script defer>)
 *
 * Public API (rarely needed)
 *   window.DroolRewards.refreshCart()   — re-fetch and re-render every instance
 *   window.DroolRewards.initAll()       — re-scan the DOM for new roots
 * ========================================================================== */

(function () {
  'use strict';

  if (window.DroolRewards) return; // idempotent — guard against double-include

  // -------------------------------------------------------------------------
  // Tiny helpers
  // -------------------------------------------------------------------------

  /** Substitutes {key} placeholders in a copy template. */
  function fmt(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (_, k) {
      return vars[k] != null ? vars[k] : '';
    });
  }

  /** HTML-escapes a value for safe insertion into innerHTML text content. */
  var ENT_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return ENT_MAP[c]; });
  }

  /** Debounce a function with a trailing-edge timer. */
  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  /**
   * Format a money value for display in copy.
   *   • Always rounded to the nearest cent (no float-precision artefacts)
   *   • Whole numbers render WITHOUT trailing ".00" — e.g. "84" not "84.00"
   *   • Partial values render with two decimals — e.g. "84.04"
   *
   * We do all our arithmetic on the major-unit value (pounds/euros) but
   * float subtraction can yield 84.03999999999999 — toFixed alone would
   * keep that, so we round to integer cents first then divide back out.
   */
  function fmtMoney(n) {
    var cents = Math.round((Number(n) || 0) * 100);
    return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
  }

  /** Parse currency text into a major-unit number. Handles US (1,234.56),
   *  EU (1.234,56), and currency-symbol prefixes. */
  function parsePriceText(text) {
    var clean = String(text || '').replace(/[^\d.,]/g, '');
    if (!clean) return null;
    var lastDot = clean.lastIndexOf('.');
    var lastComma = clean.lastIndexOf(',');
    if (lastDot > -1 && lastComma > -1) {
      if (lastDot > lastComma) clean = clean.replace(/,/g, '');
      else                     clean = clean.replace(/\./g, '').replace(',', '.');
    } else if (lastComma > -1) {
      var afterComma = clean.length - lastComma - 1;
      if (afterComma === 2) clean = clean.replace(',', '.');
      else                  clean = clean.replace(/,/g, '');
    }
    var num = parseFloat(clean);
    return isNaN(num) ? null : num;
  }

  /**
   * Read the *displayed* PDP price as a major-unit number.
   *
   * Why this exists: Drool's mrk.js sums multiple variants (the chosen
   * product variant + frame variant + framing service) into a single
   * composite price written to .mrk-main-price. The base-variant price
   * we pass into vbadges via data-variant-price is just one component
   * of that — using it underestimates the projected cart total, which
   * makes "Unlocks 2 free prints" badges fail to appear when a framed
   * selection actually crosses the threshold.
   *
   * Falls through a list of common Drool/Shopify price selectors so it
   * still works on product types where .mrk-main-price isn't used.
   */
  function readDisplayedPriceMajor() {
    var selectors = [
      '.mrk-main-price',
      '.product-details .mrk-main-price',
      '[ref="priceContainer"] .price',
      '[ref="priceContainer"]'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (!el) continue;
      var num = parsePriceText(el.textContent);
      if (num != null && num > 0) return num;
    }
    return null;
  }

  /**
   * Watch the page for ANY change to .mrk-main-price text and re-render
   * all vbadges. Set up once at init, runs forever.
   *
   * Why this is more aggressive than a targeted observer: Drool's
   * mrk.js handles frame changes silently (no dispatched event), and
   * .mrk-main-price can be replaced wholesale on variant changes. A
   * document-body observer catches every mutation regardless of where
   * the new .mrk-main-price ends up. Cost is tiny — we just compare a
   * cached text string and bail early if unchanged.
   *
   * Belt + braces — also listen for `change` events on common variant
   * input selectors (frame switcher, framing-service toggle,
   * variant-picker, option inputs) so user actions trigger an explicit
   * re-render even if the MutationObserver somehow misses.
   */
  function setupPriceObserver() {
    if (window._droolPriceObserved) return;
    window._droolPriceObserved = true;

    var rerenderAllVbadges = debounce(function () {
      var vbadges = document.querySelectorAll('[data-drool-rewards="vbadges"]');
      for (var i = 0; i < vbadges.length; i++) {
        if (vbadges[i]._inited) renderVbadges(vbadges[i]);
      }
    }, 60);

    var lastSeen = '';
    var obs = new MutationObserver(function () {
      var el = document.querySelector('.mrk-main-price');
      if (!el) return;
      var t = el.textContent;
      if (t === lastSeen) return;
      lastSeen = t;
      rerenderAllVbadges();
    });
    obs.observe(document.body, { childList: true, characterData: true, subtree: true });

    document.addEventListener('change', function (e) {
      var t = e && e.target;
      if (!t || !t.matches) return;
      if (t.matches('.mrk-frame-switcher input, [name="framing-service"], variant-picker input, [name^="options"]')) {
        setTimeout(rerenderAllVbadges, 100);
      }
    });
  }

  // -------------------------------------------------------------------------
  // Cart state (singleton shared across all instances)
  // -------------------------------------------------------------------------

  var cartTotalMajor = 0;        // cart total in pounds (or active currency unit)
  var cartTotalFetched = false;  // becomes true after first /cart.js completes
  var cartListeners = [];        // [(=>void)] — called after refreshCart resolves
  var fetchInFlight = null;      // dedupes concurrent fetches

  /**
   * Fetch /cart.js once; reuses an in-flight promise if called concurrently.
   * Notifies every registered listener after the total updates.
   */
  function refreshCart() {
    if (fetchInFlight) return fetchInFlight;
    fetchInFlight = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartTotalMajor = (cart.total_price || 0) / 100;
        cartTotalFetched = true;
        notifyListeners();
      })
      .catch(function () { cartTotalFetched = true; })
      .then(function () { fetchInFlight = null; });
    return fetchInFlight;
  }

  function notifyListeners() {
    for (var i = 0; i < cartListeners.length; i++) cartListeners[i]();
  }

  /** Coalesced refresh — rapid cart events fire only one network call. */
  var scheduleRefresh = debounce(refreshCart, 120);

  // -------------------------------------------------------------------------
  // Cart-change detection
  //
  // Different Shopify themes and cart apps dispatch different event names.
  // We listen for the common ones plus transparently watch fetch() calls to
  // /cart/* endpoints, so the bar reacts no matter how the cart was modified.
  // -------------------------------------------------------------------------

  ['cart:update', 'cart:updated', 'cart:refresh', 'theme:cart:updated', 'drool:cart:updated']
    .forEach(function (n) { document.addEventListener(n, scheduleRefresh); });

  if (typeof window.fetch === 'function') {
    var _origFetch = window.fetch;
    window.fetch = function (resource, init) {
      var url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      var p = _origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) p.then(scheduleRefresh, function () {});
      return p;
    };
  }

  window.addEventListener('pageshow', scheduleRefresh);

  // -------------------------------------------------------------------------
  // Reward state computation (pure — no DOM)
  //
  // Returns three reward objects keyed by `discount`, `prints`, `shipping`.
  // Each has { key, state, threshold, remaining } and discount adds `mode`.
  // State is one of: 'available' | 'progress' | 'applied' | 'locked'.
  // -------------------------------------------------------------------------

  /**
   * Build one reward state object. Math is done in integer cents to avoid
   * IEEE-754 artefacts (e.g. 108 - 23.96 produced 84.03999999999999 with
   * naive float subtraction).
   */
  function rewardObj(key, thresholdMajor, cartMajor, extra) {
    var thresholdCents = Math.round((thresholdMajor || 0) * 100);
    var cartCents      = Math.round((cartMajor || 0) * 100);

    var state;
    if (thresholdCents <= 0)             state = 'locked';
    else if (cartCents >= thresholdCents) state = 'applied';
    else if (cartCents === 0)             state = 'locked';
    else                                  state = 'progress';

    var o = {
      key: key,
      threshold: thresholdMajor,
      remaining: Math.max(0, thresholdCents - cartCents) / 100,
      state: state
    };
    if (extra) for (var k in extra) o[k] = extra[k];
    return o;
  }

  function computeRewards(cfg, cart) {
    var rewards = {};

    // Discount — either auto-applied on first item or threshold-based
    if (cfg.discount_mode === 'auto') {
      rewards.discount = {
        key: 'discount', mode: 'auto',
        threshold: 0, remaining: 0,
        state: cart === 0 ? 'available' : 'applied'
      };
    } else {
      rewards.discount = rewardObj('discount', cfg.discount_threshold || 0, cart, { mode: 'threshold' });
    }

    rewards.prints = rewardObj('prints', cfg.prints_threshold || 0, cart);

    if (cfg.shipping_enabled) {
      rewards.shipping = rewardObj('shipping', cfg.shipping_threshold || 0, cart);
    }
    return rewards;
  }

  function rewardList(rewards) {
    var items = [rewards.discount, rewards.prints];
    if (rewards.shipping) items.push(rewards.shipping);
    return items;
  }

  /** First reward still being chased (lowest threshold not yet met). */
  function nextChase(rewards) {
    return rewardList(rewards)
      .filter(function (i) { return i.threshold > 0; })
      .sort(function (a, b) { return a.threshold - b.threshold; })
      .find(function (i) { return i.state !== 'applied'; });
  }

  // -------------------------------------------------------------------------
  // Copy resolution (pure — no DOM)
  //
  // All user-facing strings come from cfg; renderers never hardcode copy.
  // Fallback defaults live here in case a merchant clears a setting.
  // -------------------------------------------------------------------------

  function symbol(cfg) { return cfg.currency_symbol || ''; }

  function pillLabel(item, cfg) {
    var s = symbol(cfg);
    var t = item.threshold;
    var r = fmtMoney(item.remaining);
    var pct = cfg.discount_percent != null ? cfg.discount_percent : 20;
    var vars = { symbol: s, t: t, r: r, percent: pct };

    if (item.key === 'discount') {
      if (item.mode === 'auto') {
        return fmt(item.state === 'applied'
          ? (cfg.copy_pill_discount_applied || '{percent}% applied in cart')
          : (cfg.copy_pill_discount_first   || '{percent}% off your first order'), vars);
      }
      if (item.state === 'applied') return fmt(cfg.copy_pill_discount_applied || '{percent}% applied in cart', vars);
      if (item.state === 'locked')  return fmt(cfg.copy_pill_discount_locked   || '{percent}% off at {symbol}{t}', vars);
      return                                fmt(cfg.copy_pill_discount_progress || 'Add {symbol}{r} for {percent}% off', vars);
    }

    if (item.key === 'prints') {
      vars.n = 2;
      if (item.state === 'applied') return fmt(cfg.copy_pill_prints_unlocked || '{n} free prints unlocked', vars);
      if (item.state === 'locked')  return fmt(cfg.copy_pill_prints_locked   || '2 free prints at {symbol}{t}', vars);
      return                               fmt(cfg.copy_pill_prints_progress || 'Add {symbol}{r} for 2 free prints', vars);
    }

    // shipping
    if (item.state === 'applied') return cfg.copy_pill_shipping_unlocked || 'Free shipping unlocked';
    if (item.state === 'locked')  return fmt(cfg.copy_pill_shipping_locked   || 'Free shipping at {symbol}{t}', vars);
    return                               fmt(cfg.copy_pill_shipping_progress || 'Add {symbol}{r} for free shipping', vars);
  }

  /** Map a state name to its short CSS class suffix. */
  var STATE_CLASS = { available: 'on', progress: 'prog', applied: 'done', locked: 'lock' };

  /** Build the action-line copy (the "Add £X for ..." sentence). */
  function buildAction(cfg, rewards, cart) {
    var chase = nextChase(rewards);
    var s = symbol(cfg);
    var t = cfg.prints_threshold || 0;
    var w = cfg.prints_worth || 0;
    var inCart = cfg.surface === 'cart';
    var showWorth = !!cfg.show_worth;

    if (!chase) {
      // All thresholds met — celebrate the prints reward
      var earned = (t > 0)
        ? ((inCart && cfg.extra_prints) ? Math.floor(cart / t) * 2 : 2)
        : 0;
      var doneTemplate = inCart
        ? (cfg.copy_action_done_cart  || 'Select your <b>{n} free {size}prints</b> below')
        : (cfg.copy_action_done_other || 'Select your <b>{n} free prints</b> in cart');
      var size = (inCart && cfg.print_size_label) ? cfg.print_size_label + ' ' : '';
      var primary = fmt(doneTemplate, { n: earned, size: size });
      var extra = (inCart && cfg.extra_prints && t > 0)
        ? fmt(cfg.copy_action_bonus || 'Get <b>2 additional free prints</b> for every <b>{symbol}{t}</b> added', { symbol: s, t: t })
        : '';
      return { html: primary, extra: extra, done: true };
    }

    // Reward phrase — chase-aware, optional "worth £X" suffix
    var pct = cfg.discount_percent != null ? cfg.discount_percent : 20;
    var rewardLabel;
    if (chase.key === 'prints') {
      rewardLabel = showWorth
        ? '<b>2 free prints</b> worth <b>' + s + w + '</b>'
        : '<b>2 free prints</b>';
    } else if (chase.key === 'shipping') {
      rewardLabel = '<b>free shipping</b>';
    } else {
      rewardLabel = '<b>' + pct + '% off</b>';
    }

    if (cart === 0) {
      return {
        html: fmt(cfg.copy_action_zero || 'Spend <b>{symbol}{t}</b> to unlock {reward}', { symbol: s, t: chase.threshold, reward: rewardLabel }),
        extra: '', done: false
      };
    }
    return {
      html: fmt(cfg.copy_action_short || 'Add <b>{symbol}{r}</b> more to unlock {reward}', { symbol: s, r: fmtMoney(chase.remaining), reward: rewardLabel }),
      extra: '', done: false
    };
  }

  /** Build the small notice line beneath the bar (e.g. "20% off applied"). */
  function buildNotice(cfg, rewards) {
    var inCart = cfg.surface === 'cart';
    var s = symbol(cfg);
    var pct = cfg.discount_percent != null ? cfg.discount_percent : 20;
    var vars = { symbol: s, percent: pct, t: rewards.discount.threshold };
    var text, muted = false, check = false;

    if (rewards.discount.mode === 'auto') {
      if (inCart) {
        text = fmt(rewards.discount.state === 'applied'
          ? (cfg.copy_notice_cart_applied || '{percent}% off applied in cart')
          : (cfg.copy_notice_cart_empty   || '{percent}% off applies automatically when you add an item'), vars);
      } else {
        text = fmt(cfg.copy_notice_other || '{percent}% off applies automatically in cart for first-time customers', vars);
      }
      check = true;
    } else {
      if (rewards.discount.state === 'applied') {
        text  = fmt(cfg.copy_pill_discount_applied || '{percent}% applied in cart', vars);
        check = true;
      } else {
        text  = fmt(cfg.copy_notice_threshold || 'Spend {symbol}{t} for {percent}% off (first-time customers)', vars);
        muted = true;
      }
    }
    return '<div class="drool-rb__notice' +
      (check ? ' drool-rb__notice--check' : '') +
      (muted ? ' drool-rb__notice--muted' : '') +
      '">' + text + '</div>';
  }

  // -------------------------------------------------------------------------
  // Renderers
  //
  // Each renderer is pure-ish: given (root, cfg, cart) it produces HTML and
  // writes it to root.innerHTML — but only if the HTML actually changed since
  // last render. This keeps the cart drawer re-flow cheap when many bars are
  // mounted on the page.
  // -------------------------------------------------------------------------

  function setHTML(root, html) {
    if (root._lastHTML === html) return;
    root._lastHTML = html;
    root.innerHTML = html;
  }

  /** Centered box with pills + bounded progress bar + action + notice. */
  function renderBox(root) {
    var cfg = root._cfg;
    var cart = cartTotalMajor;
    var rewards = computeRewards(cfg, cart);
    var items = rewardList(rewards);
    var chase = nextChase(rewards);

    var pillsHTML = items.map(function (item) {
      return '<span class="drool-rb__pill drool-rb__pill--' + STATE_CLASS[item.state] + '">' +
        esc(pillLabel(item, cfg)) +
      '</span>';
    }).join('');

    var barHTML;
    if (!chase) {
      barHTML = '<div class="drool-rb__bar drool-rb__bar--unlocked">' +
        '<div class="drool-rb__track"><div class="drool-rb__fill" style="width:100%"></div></div>' +
      '</div>';
    } else {
      var s = symbol(cfg);
      var fillPct = Math.min(100, (cart / chase.threshold) * 100);
      barHTML = '<div class="drool-rb__bar">' +
        '<span class="drool-rb__bound drool-rb__bound--left">' + esc(s + '0') + '</span>' +
        '<div class="drool-rb__track"><div class="drool-rb__fill" style="width:' + fillPct + '%"></div></div>' +
        '<span class="drool-rb__bound drool-rb__bound--right">' + esc(s + chase.threshold) + '</span>' +
      '</div>';
    }

    var action = buildAction(cfg, rewards, cart);
    var bandCls = 'drool-rb__band' + (cfg.surface === 'collection' && cfg.sticky ? ' drool-rb__band--sticky' : '');
    var boxCls = 'drool-rb__box' + (cfg.compact ? ' drool-rb__box--compact' : '');

    setHTML(root,
      '<div class="' + bandCls + '">' +
        '<div class="' + boxCls + '">' +
          '<div class="drool-rb__pills">' + pillsHTML + '</div>' +
          barHTML +
          '<div class="drool-rb__action' + (action.done ? ' drool-rb__action--done' : '') + '">' + action.html + '</div>' +
          buildNotice(cfg, rewards) +
          (action.extra ? '<div class="drool-rb__bonus">' + action.extra + '</div>' : '') +
        '</div>' +
      '</div>'
    );
  }

  /**
   * PDP variant badges — sit next to the price and react to variant changes.
   *
   * Notes that differ from the centered box:
   *   • Prints are always single-threshold (extra-prints messaging is cart-only)
   *   • "short" branch uses (threshold - cart), not (threshold - projected), so
   *     the merchant says "add £X to your cart" rather than "add £Y if you also
   *     buy this variant" — clearer call to action
   *   • {worth_paren} expands to " (worth £X)" or "" depending on show_worth
   *   • {worth_suffix} expands to " worth £X" or "" depending on show_worth
   */
  function renderVbadges(root) {
    var cfg = root._cfg;
    var cart = cartTotalMajor;
    // Prefer the live displayed total from .mrk-main-price (handles Drool's
    // composite frame + framing service + base-product price). Falls back
    // to the data-variant-price attribute for non-Drool themes.
    var displayed = readDisplayedPriceMajor();
    var variantPrice = displayed != null
      ? displayed
      : parseFloat(root.getAttribute('data-variant-price') || '0');
    var projected = cart + variantPrice;
    var rewards = computeRewards(cfg, cart);
    var s = symbol(cfg);
    var w = cfg.prints_worth || 0;
    var showWorth = !!cfg.show_worth;
    var worthParen  = showWorth ? ' (worth ' + s + w + ')' : '';
    var worthSuffix = showWorth ? ' worth ' + s + w : '';
    var badges = [];

    function badge(cls, label) {
      badges.push('<span class="drool-rb-vbadge drool-rb-vbadge--' + cls + '">' + esc(label) + '</span>');
    }

    // --- Discount ---
    var pct = cfg.discount_percent != null ? cfg.discount_percent : 20;
    var dVars = { symbol: s, percent: pct };
    if (rewards.discount.mode === 'auto') {
      badge('earned', fmt(cfg.copy_pill_discount_applied || '{percent}% applied in cart', dVars));
    } else if (rewards.discount.threshold > 0) {
      var dt = rewards.discount.threshold;
      var dWasMet = cart >= dt;
      var dNowMet = projected >= dt;
      if (!dWasMet && dNowMet)  badge('unlock', fmt(cfg.copy_vbadge_disc_unlock || 'Unlocks {percent}% off', dVars));
      else if (dWasMet)         badge('earned', fmt(cfg.copy_vbadge_disc_earned || '{percent}% off already applied', dVars));
      else {
        dVars.r = fmtMoney(rewards.discount.remaining);
        badge('short',  fmt(cfg.copy_vbadge_disc_short || 'Add {symbol}{r} more for {percent}% off', dVars));
      }
    }

    // --- Prints (single-threshold only) ---
    var pt = rewards.prints.threshold;
    if (pt > 0) {
      var pWasMet = cart >= pt;
      var pNowMet = projected >= pt;
      if (!pWasMet && pNowMet)  badge('unlock', fmt(cfg.copy_vbadge_prints_unlock || 'Unlocks 2 free prints{worth_paren}', { worth_paren: worthParen }));
      else if (pWasMet)         badge('earned', fmt(cfg.copy_vbadge_prints_earned || '2 free prints unlocked{worth_suffix}', { worth_suffix: worthSuffix }));
      else                      badge('short',  fmt(cfg.copy_vbadge_prints_short  || 'Add {symbol}{r} more for 2 free prints', { symbol: s, r: fmtMoney(rewards.prints.remaining) }));
    }

    // --- Shipping ---
    if (rewards.shipping && rewards.shipping.threshold > 0) {
      var st = rewards.shipping.threshold;
      var sWasMet = cart >= st;
      var sNowMet = projected >= st;
      if (!sWasMet && sNowMet)  badge('unlock', cfg.copy_vbadge_ship_unlock || 'Unlocks free shipping');
      else if (sWasMet)         badge('earned', cfg.copy_vbadge_ship_earned || 'Free shipping already unlocked');
      else                      badge('short',  fmt(cfg.copy_vbadge_ship_short || 'Add {symbol}{r} more for free shipping', { symbol: s, r: fmtMoney(rewards.shipping.remaining) }));
    }

    setHTML(root, badges.join(''));
  }

  // -------------------------------------------------------------------------
  // Mount + lifecycle
  // -------------------------------------------------------------------------

  /**
   * Position the bar root inside the right ancestor.
   *
   * Why: Shopify wraps every section in <section class="shopify-section">,
   * which becomes the containing block for any child using position: sticky.
   * That section is only as tall as the bar itself — so sticky has zero room
   * to slide. Hoisting the root into <main> (or <body>) fixes it.
   *
   * Also supports an explicit `target_selector` for cases where the bar
   * should be injected into a specific element (e.g. the cart drawer body).
   */
  /**
   * Drop `root` into the configured target, using one of the five positions:
   *   before | prepend | append | after | replace
   * If no target selector is set and this is a sticky collection placement,
   * hoist into <main> so the sticky shim has a usable containing block.
   */
  function placeRoot(root) {
    if (root._placed) return;
    var sel = root._cfg.target_selector;
    if (sel) {
      var target = document.querySelector(sel);
      if (!target) return; // selector miss — leave the root where it sits
      var pos = root._cfg.target_position || 'append';
      switch (pos) {
        case 'before':
          target.parentNode.insertBefore(root, target);
          break;
        case 'prepend':
          target.insertBefore(root, target.firstChild);
          break;
        case 'after':
          if (target.nextSibling) target.parentNode.insertBefore(root, target.nextSibling);
          else                    target.parentNode.appendChild(root);
          break;
        case 'replace':
          target.parentNode.replaceChild(root, target);
          break;
        case 'append':
        default:
          target.appendChild(root);
      }
      root._placed = true;
      return;
    }
    // Auto-hoist for sticky collection placements
    if (root._cfg.surface === 'collection' && root._cfg.sticky) {
      var host = document.querySelector('main') || document.body;
      if (host && root.parentNode !== host) {
        host.insertBefore(root, host.firstChild);
        root._placed = true;
      }
    }
  }

  /** Capture the originating Shopify section ID so we can remove this root
   *  on shopify:section:unload (otherwise it duplicates every time the
   *  merchant changes a setting in the editor). */
  function captureSectionId(root) {
    var section = root.closest('[id^="shopify-section-"]');
    return section ? section.id.replace(/^shopify-section-/, '') : null;
  }

  /**
   * JS-based sticky behaviour.
   *
   * Why we don't use CSS position: sticky: most themes (Drool included) set
   * `overflow-x: hidden` on <html>/<body> to prevent horizontal scroll from
   * mobile menus and carousels. That overflow makes <body> a scroll container,
   * which traps any descendant's sticky inside a viewport-tall containing
   * block — sticky has zero room to slide.
   *
   * Workaround: watch a 1px sentinel placed just above the bar with an
   * IntersectionObserver. When the sentinel scrolls out of view, add a
   * .drool-rb--fixed class to the root — CSS then positions the band as
   * fixed at the configured top offset. A spacer keeps the document layout
   * from jumping when the bar leaves the flow.
   */
  function setupStickyShim(root) {
    if (root._stickyShim || !('IntersectionObserver' in window)) return;
    root._stickyShim = true;

    var topOffset = parseInt((root.style.getPropertyValue('--drool-rb-sticky-top') || '0').replace('px', ''), 10) || 0;

    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;width:100%;margin:0;padding:0;visibility:hidden;';
    root.parentNode.insertBefore(sentinel, root);
    root._stickySentinel = sentinel;

    var spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.cssText = 'display:none;margin:0;padding:0;';
    root.parentNode.insertBefore(spacer, root);
    root._stickySpacer = spacer;

    function syncSpacerHeight() { spacer.style.height = root.offsetHeight + 'px'; }
    function fix() {
      if (root.classList.contains('drool-rb--fixed')) return;
      syncSpacerHeight();
      spacer.style.display = 'block';
      root.classList.add('drool-rb--fixed');
    }
    function unfix() {
      if (!root.classList.contains('drool-rb--fixed')) return;
      spacer.style.display = 'none';
      root.classList.remove('drool-rb--fixed');
    }

    // Trigger fix when sentinel scrolls above the configured top offset
    var observer = new IntersectionObserver(function (entries) {
      var rect = entries[0].boundingClientRect;
      if (rect.top < topOffset) fix();
      else unfix();
    }, { rootMargin: '-' + topOffset + 'px 0px 0px 0px', threshold: [0, 1] });
    observer.observe(sentinel);

    // Keep spacer height in sync with bar height (resize, copy changes)
    window.addEventListener('resize', function () {
      if (root.classList.contains('drool-rb--fixed')) syncSpacerHeight();
    }, { passive: true });
  }

  /** Parse a root's data-config, place it, and wire its renderer to cart updates. */
  function initRoot(root, kind) {
    if (root._inited) return;
    root._inited = true;

    try {
      root._cfg = JSON.parse(root.getAttribute('data-config') || '{}');
    } catch (e) {
      if (window.console && window.console.warn) {
        window.console.warn('[drool-rewards] invalid config JSON on element', root, e);
      }
      root._cfg = {};
    }

    // Capture section ID BEFORE the bar gets hoisted/moved out of its
    // section wrapper — we need it to clean up on shopify:section:unload.
    root._sectionId = captureSectionId(root);
    root.setAttribute('data-drool-section', root._sectionId || '');

    placeRoot(root);

    // Sticky behaviour: set up the JS-fixed shim (CSS sticky is unreliable
    // under themes with overflow-x: hidden on body/html).
    if (kind === 'bar' && root._cfg.surface === 'collection' && root._cfg.sticky) {
      setupStickyShim(root);
    }

    // Combined tile-badge injection: when the bar section has tile injection
    // enabled, reuse the rewards-bar's config (currency, thresholds, mode)
    // for the tile badges too. Keeps the merchant managing one section instead
    // of two — same per-currency settings drive both.
    if (kind === 'bar' && root._cfg.tile_enabled && root._cfg.tile_target_selector) {
      var c = root._cfg;
      setupTileInjector(root, {
        target_selector:    c.tile_target_selector,
        target_position:    c.tile_target_position,
        discount_mode:      c.discount_mode,
        discount_threshold: c.discount_threshold,
        discount_percent:   c.discount_percent,
        currency_symbol:    c.currency_symbol,
        badge_text:         c.tile_badge_text,
        badge_text_mobile:  c.tile_badge_text_mobile,
        badge_suffix:       c.tile_badge_suffix,
        show_micro:         c.tile_show_micro,
        micro_text:         c.tile_micro_text,
        micro_text_mobile:  c.tile_micro_text_mobile,
        mobile_stacked:     c.tile_mobile_stacked
      }, root._sectionId);
    }

    /**
     * Drool's cart-items-component handles cart updates via morphSection,
     * which replaces the entire <cart-items-component> and everything
     * inside it. If the bar was JS-injected at a selector that lives
     * inside that morphed zone (e.g. .cart-drawer__header, cart-products,
     * anywhere inside cart-items-component), the bar's root gets wiped
     * from the DOM. Re-inject before re-rendering so the bar reappears
     * in the right slot after every cart change.
     */
    function ensurePlaced() {
      if (!document.contains(root) && root._cfg && root._cfg.target_selector) {
        root._placed = false;
        placeRoot(root);
      }
    }

    var render = kind === 'bar'
      ? function () { ensurePlaced(); renderBox(root); }
      : function () { ensurePlaced(); renderVbadges(root); };
    cartListeners.push(render);

    if (kind === 'vbadges') {
      // The theme dispatches `variant:update` with the active variant. Capture
      // the new price and re-render. Stored on the element so cart-listener
      // re-renders also pick up the latest variant price.
      //
      // Drool's theme (assets/product-price.js) does
      //   currentPrice.replaceWith(newPrice)
      // on every variant change — replacing the entire `[ref="priceContainer"]`
      // div with freshly server-rendered HTML. Any vbadges root injected INSIDE
      // priceContainer (e.g. target=.mrk-main-price or [ref="priceContainer"])
      // gets detached from the DOM. setTimeout 0 waits for the theme to finish,
      // then re-runs placeRoot if our root no longer lives in the document.
      document.addEventListener('variant:update', function (e) {
        var v = e && e.detail && e.detail.resource;
        if (v && typeof v.price === 'number') {
          root.setAttribute('data-variant-price', (v.price / 100).toString());
        }
        setTimeout(function () {
          if (!document.contains(root) && root._cfg && root._cfg.target_selector) {
            root._placed = false;
            placeRoot(root);
          }
          render();
        }, 0);
      });
    }

    if (cartTotalFetched) render();
    else refreshCart();
  }

  function initAll() {
    var bars    = document.querySelectorAll('[data-drool-rewards="bar"]');
    var vbadges = document.querySelectorAll('[data-drool-rewards="vbadges"]');
    var tiles   = document.querySelectorAll('[data-drool-rewards="tiles"]');
    for (var i = 0; i < bars.length;    i++) initRoot(bars[i],    'bar');
    for (var j = 0; j < vbadges.length; j++) initRoot(vbadges[j], 'vbadges');
    for (var k = 0; k < tiles.length;   k++) initTileInjector(tiles[k]);
    if (vbadges.length) setupPriceObserver();
  }

  // -------------------------------------------------------------------------
  // Tile-badge injector
  //
  // The collection-tile badge is a small "20% off in cart" pill that should
  // sit next to every product price. When Boost (or a similar app) owns the
  // product card markup, there's no Shopify product-card.liquid to drop a
  // snippet into — instead this section places a hidden config element on
  // the page, and the JS queries every selector match and injects an inline
  // badge into each one.
  //
  // Re-injection triggers (in priority order):
  //   1. Boost's `boost-sd:product-list:render[ed]` events
  //   2. A MutationObserver on the closest grid container (handles other
  //      apps that don't dispatch events)
  // -------------------------------------------------------------------------

  /**
   * The badge (disc) — sits inline next to the price text. The micro line
   * gets injected separately as a sibling of the target (see inject() below)
   * so it can land on its own line beneath the whole price+badge row.
   */
  function buildTileBadge(cfg, sectionId) {
    var sym  = cfg.currency_symbol || '';
    var t    = cfg.discount_threshold || 0;
    var pct  = cfg.discount_percent != null ? cfg.discount_percent : 20;
    var auto = cfg.discount_mode !== 'threshold';
    var vars = { symbol: sym, t: t, percent: pct };
    var suffix = auto ? '' : fmt(cfg.badge_suffix || ' at {symbol}{t}+', vars);
    var desktop = fmt(cfg.badge_text || '{percent}% off in cart', vars);
    // Mobile copy falls back to desktop if not set.
    var mobile  = fmt(cfg.badge_text_mobile || cfg.badge_text || '{percent}% off in cart', vars);

    var span = document.createElement('span');
    span.className = 'drool-rb-tile' + (cfg.mobile_stacked ? ' drool-rb-tile--mobile-stacked' : '');
    span.setAttribute('data-drool-tile-from', sectionId || '');

    var discCls = 'drool-rb-tile__disc' + (auto ? ' drool-rb-tile__disc--applied' : '');
    span.innerHTML =
      '<span class="' + discCls + '">' +
        '<span class="drool-rb-tile__resp--desktop">' + esc(desktop + suffix) + '</span>' +
        '<span class="drool-rb-tile__resp--mobile">'  + esc(mobile  + suffix) + '</span>' +
      '</span>';
    return span;
  }

  /** Standalone micro line — inserted as a sibling of the target so it
   *  lands on a new line below the entire price + badge row. Renders both
   *  desktop + mobile copy with CSS-driven viewport swap. */
  function buildTileMicro(cfg, sectionId) {
    var span = document.createElement('span');
    span.className = 'drool-rb-tile__micro-line';
    span.setAttribute('data-drool-tile-from', sectionId || '');
    var desktop = cfg.micro_text || '';
    var mobile  = cfg.micro_text_mobile || cfg.micro_text || '';
    span.innerHTML =
      '<span class="drool-rb-tile__resp--desktop">' + esc(desktop) + '</span>' +
      '<span class="drool-rb-tile__resp--mobile">'  + esc(mobile)  + '</span>';
    return span;
  }

  function injectAt(target, pos, node) {
    switch (pos) {
      case 'before':  target.parentNode.insertBefore(node, target); return;
      case 'prepend': target.insertBefore(node, target.firstChild); return;
      case 'after':
        if (target.nextSibling) target.parentNode.insertBefore(node, target.nextSibling);
        else                    target.parentNode.appendChild(node);
        return;
      case 'replace': target.parentNode.replaceChild(node, target); return;
      case 'append':
      default: target.appendChild(node);
    }
  }

  /** Returns true if this target (or the slot the badge would land in)
   *  already has a Drool tile badge sitting in it. */
  function hasTileAt(target, pos) {
    if (pos === 'replace') return target.classList && target.classList.contains('drool-rb-tile');
    if (pos === 'before')  return target.previousElementSibling && target.previousElementSibling.classList.contains('drool-rb-tile');
    if (pos === 'after')   return target.nextElementSibling && target.nextElementSibling.classList.contains('drool-rb-tile');
    return target.querySelector(':scope > .drool-rb-tile') !== null;
  }

  /**
   * The actual inject + observe machinery, factored out so it can be reused:
   *   • initTileInjector() — for the standalone Drool · Tile badges section
   *   • initRoot('bar')   — when the rewards bar section also has tile
   *                         injection enabled via cfg.tile_enabled
   */
  function setupTileInjector(root, cfg, sectionId) {
    if (!cfg || !cfg.target_selector) return;
    var pos = cfg.target_position || 'append';

    function inject() {
      var targets = document.querySelectorAll(cfg.target_selector);
      for (var i = 0; i < targets.length; i++) {
        var target = targets[i];
        // Attribute-based dedup: stamp the target itself after first
        // injection. More robust than the sibling-chain hasTileAt check —
        // third-party scripts (Visually.io, banners, etc.) can insert
        // their own elements between our tile and the target, making the
        // sibling check think nothing's injected yet → infinite loop as
        // each component's MutationObserver reacts to the other's
        // mutations. The attribute survives any number of intermediate
        // siblings. Cleared on section unload so re-mounts work.
        if (target.getAttribute('data-drool-tile-injected') || hasTileAt(target, pos)) continue;
        target.setAttribute('data-drool-tile-injected', sectionId || '1');

        // 1) Insert the badge at the configured position
        injectAt(target, pos, buildTileBadge(cfg, sectionId));

        // 2) Insert the micro line as a sibling immediately AFTER the
        //    target so it flows on the line below the price + badge.
        //    (replace mode skips this — the target is gone.)
        if (cfg.show_micro && cfg.micro_text && pos !== 'replace') {
          var micro = buildTileMicro(cfg, sectionId);
          if (target.nextSibling) target.parentNode.insertBefore(micro, target.nextSibling);
          else                    target.parentNode.appendChild(micro);
        }
      }
    }

    inject();

    // Boost re-renders the grid on filter/sort/infinite-scroll
    ['boost-sd:product-list:render', 'boost-sd:product-list:rendered'].forEach(function (n) {
      document.addEventListener(n, inject);
    });

    // Generic fallback — observe the closest plausible container for added
    // product cards. Self-mutations (us injecting badges) are filtered out.
    var container = document.querySelector('.boost-sd__product-list, .boost-sd-container, main') || document.body;
    var debounced = debounce(inject, 80);
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && n.classList &&
              !n.classList.contains('drool-rb-tile') &&
              !n.classList.contains('drool-rb-tile__micro-line')) {
            debounced();
            return;
          }
        }
      }
    });
    mo.observe(container, { childList: true, subtree: true });
    root._tileObserver = mo;
  }

  /** Thin entry-point for the standalone Drool · Tile badges section. */
  function initTileInjector(root) {
    if (root._inited) return;
    root._inited = true;
    try { root._cfg = JSON.parse(root.getAttribute('data-config') || '{}'); }
    catch (e) { root._cfg = {}; }
    root._sectionId = captureSectionId(root);
    root.setAttribute('data-drool-section', root._sectionId || '');
    setupTileInjector(root, root._cfg, root._sectionId);
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme-editor live reload re-renders sections without a full page reload
  document.addEventListener('shopify:section:load', initAll);

  /**
   * Drool's mrk.js fires `mrkVariantChanged` after recalculating the
   * composite price (frame variant + framing service + base product) and
   * writing the new total into .mrk-main-price. variant:update DOESN'T
   * fire for frame/framing-service changes, so we need this extra hook to
   * keep the vbadges in sync with the displayed total.
   *
   * 50ms delay lets the theme finish its DOM writes before we re-read
   * .mrk-main-price.
   */
  document.addEventListener('mrkVariantChanged', function () {
    setTimeout(function () {
      var vbadges = document.querySelectorAll('[data-drool-rewards="vbadges"]');
      for (var i = 0; i < vbadges.length; i++) {
        if (vbadges[i]._inited) renderVbadges(vbadges[i]);
      }
    }, 50);
  });

  /**
   * When the theme editor reloads a section, any root we previously hoisted
   * out of that section sits orphaned in <main> (or wherever it was injected
   * via target_selector). Remove it — along with its sticky sentinel and
   * spacer — so the new section's render doesn't double up.
   */
  document.addEventListener('shopify:section:unload', function (e) {
    var sid = e && e.detail && e.detail.sectionId;
    if (!sid) return;
    // Remove the hidden config / bar root, plus its sticky sentinel + spacer
    // + any MutationObserver attached for tile injection.
    document.querySelectorAll('[data-drool-section="' + sid + '"]').forEach(function (root) {
      if (root._stickySentinel) root._stickySentinel.remove();
      if (root._stickySpacer)   root._stickySpacer.remove();
      if (root._tileObserver)   root._tileObserver.disconnect();
      root.remove();
    });
    // Remove every injected tile badge originating from this section
    document.querySelectorAll('[data-drool-tile-from="' + sid + '"]').forEach(function (n) {
      n.remove();
    });
    // Clear the injection-marker attribute from targets so re-mount
    // (e.g. theme editor live reload) can inject fresh tiles
    document.querySelectorAll('[data-drool-tile-injected="' + sid + '"]').forEach(function (n) {
      n.removeAttribute('data-drool-tile-injected');
    });
  });

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  window.DroolRewards = {
    initAll: initAll,
    refreshCart: refreshCart
  };
})();
